import React from 'react';
import {
  Box, Dialog, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { CloseRounded as CloseIcon } from '@mui/icons-material';

const fmt = (n) => new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const num = (v) => Number(v) || 0;

const BORDER = '#e7e5e4';
const NUM_SX = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const HEAD_SX = { py: 1, px: 1.5, fontSize: '0.8rem', fontWeight: 600, color: '#57534e', bgcolor: '#fafaf9', borderColor: BORDER, whiteSpace: 'nowrap' };
const CELL_SX = { py: 1.25, px: 1.5, fontSize: '0.9rem', borderColor: '#f5f5f4' };
// เส้นแบ่งกลุ่มคอลัมน์ (รายได้ | หัก | สรุป)
const GROUP_EDGE = { borderLeft: `1px solid ${BORDER}` };

// คอลัมน์แบ่ง 2 กลุ่มให้อ่านตามที่มาของเงิน: รายได้ − หัก = ที่ต้องจ่าย ("ภาษี" ตารางหลักไม่มี แต่ต้องมีถึงจะเห็น
// ที่มาของ "ที่ต้องจ่าย" ครบ)
const INCOME_COLS = [
  { key: 'salary', label: 'ค่าแรง' },
  { key: 'ot', label: 'OT' },
  { key: 'allowance', label: 'เบี้ยเลี้ยง' },
  { key: 'fa', label: 'ค่าออกสนาม' },
  { key: 'bonus', label: 'เงินได้อื่นๆ' },
];
const DEDUCT_COLS = [
  { key: 'sso', label: 'ปกส.' },
  { key: 'tax', label: 'ภาษี' },
  { key: 'crl', label: 'หัก CRL' },
  { key: 'other', label: 'หักอื่นๆ' },
];

// แยกยอดของ 1 โครงการ (แถวจาก TimestampProjectCost) ออกเป็นรายพนักงาน:
// - ค่าแรง/OT/เบี้ยเลี้ยง/ค่าออกสนาม รวมจาก row.employees (DAILY = 1 entry ต่อวันที่ลงเวลา, FULLTIME = 1 entry ต่อโครงการ)
// - ปกส./ภาษี/CRL/หักอื่นๆ/เงินได้อื่นๆ จาก row.employee_deductions
// - ที่ต้องจ่าย จาก row.employee_payables (งวดที่บันทึกก่อนมี field นี้ = null → แสดง —)
// - ต้นทุน = ค่าแรง + OT + ปกส. + เบี้ยเลี้ยง + ค่าออกสนาม + เงินได้อื่นๆ (สูตรเดียวกับ projectCostFormula.js)
function buildEmployeeRows(row, nameMap) {
  const byEmp = {};
  const get = (id) => {
    const key = String(id);
    if (!byEmp[key]) {
      byEmp[key] = {
        id: key,
        name: nameMap?.[key] || '',
        empType: '',
        days: 0,
        salary: 0,
        ot: 0,
        allowance: 0,
        fa: 0,
        bonus: 0,
        sso: 0,
        tax: 0,
        crl: 0,
        other: 0,
        payable: null,
      };
    }
    return byEmp[key];
  };
  (row.employees || []).forEach((e) => {
    const r = get(e.employee_id);
    if (e.name) r.name = e.name;
    if (e.emp_type) r.empType = e.emp_type;
    // วันที่ลงเวลาที่โครงการนี้ / วันที่ลงเวลาทั้งหมดของคนนี้ในงวด (ทุก entry ของคนเดียวกันได้ค่าเท่ากัน;
    // งวดที่บันทึกไว้ก่อนมี field นี้ = undefined → ไม่แสดง)
    if (e.days_total != null) {
      r.projDays = num(e.days_in_project);
      r.totalDays = num(e.days_total);
    }
    r.salary += num(e.amount);
    r.ot += num(e.ot_amount);
    r.allowance += num(e.allowance);
    r.fa += num(e.field_allowance_amount);
    if (num(e.amount) > 0 || num(e.ot_amount) > 0 || num(e.field_allowance_amount) > 0) r.days += 1;
  });
  (row.employee_deductions || []).forEach((d) => {
    const r = get(d.employee_id);
    r.bonus += num(d.bonus);
    r.sso += num(d.sso);
    r.tax += num(d.tax);
    r.crl += num(d.crl);
    r.other += num(d.other);
  });
  const hasPayables = Array.isArray(row.employee_payables) && row.employee_payables.length > 0;
  if (hasPayables) {
    row.employee_payables.forEach((p) => {
      const r = get(p.employee_id);
      r.payable = (r.payable || 0) + num(p.amount);
    });
  }
  const rows = Object.values(byEmp).map((r) => ({
    ...r,
    payable: hasPayables ? r.payable || 0 : null,
    income: r.salary + r.ot + r.allowance + r.fa + r.bonus,
    deduct: r.sso + r.tax + r.crl + r.other,
    cost: r.salary + r.ot + r.sso + r.allowance + r.fa + r.bonus,
  }));
  rows.sort((a, b) => b.cost - a.cost);
  return { rows, hasPayables };
}

function Stat({ label, value, color, hint }) {
  return (
    <Box sx={{ flex: '1 1 160px', minWidth: 0, px: 2.5, py: 1.75, '& + &': { borderLeft: `1px solid ${BORDER}` } }}>
      <Typography sx={{ fontSize: '0.8rem', color: '#78716c' }}>{label}</Typography>
      <Typography sx={{ ...NUM_SX, fontSize: '1.35rem', fontWeight: 700, color: color || '#1c1917', lineHeight: 1.35 }}>{value}</Typography>
      {hint && <Typography sx={{ fontSize: '0.75rem', color: '#a8a29e' }}>{hint}</Typography>}
    </Box>
  );
}

// ค่าในเซลล์ตัวเลข — 0 แสดง "—" จางๆ, กลุ่มหักแสดงเครื่องหมาย −
function Money({ value, deduct, color }) {
  if (Math.abs(value) < 0.005) return <Box component="span" sx={{ color: '#d6d3d1' }}>—</Box>;
  return (
    <Box component="span" sx={{ color: color || (deduct ? '#78716c' : '#1c1917') }}>
      {deduct ? `−${fmt(value)}` : fmt(value)}
    </Box>
  );
}

// modal รายงานต่อโครงการ — เปิดจากปุ่มไอคอนในคอลัมน์ "โครงการ" ของตาราง "ค่าแรงพนักงาน" (TimestampProjectCost.js)
// แสดงว่ายอดแต่ละคอลัมน์ของโครงการนั้นมาจากพนักงานคนไหนเท่าไร
export default function ProjectCostReportDialog({ row, nameMap, onClose }) {
  const open = !!row;
  const { rows, hasPayables } = row ? buildEmployeeRows(row, nameMap) : { rows: [], hasPayables: false };
  const total = (key) => rows.reduce((s, r) => s + num(r[key]), 0);
  const totalCost = total('cost');
  const payableSum = total('payable');
  const payableMismatch = hasPayables && Math.abs(payableSum - num(row?.total_payable)) > 0.05;
  const dailyCount = rows.filter((r) => r.empType && r.empType !== 'FULLTIME').length;
  const monthlyCount = rows.filter((r) => r.empType === 'FULLTIME').length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2 } }}>
      {row && (
        <>
          <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#78716c' }}>รายงานค่าแรงตามโครงการ</Typography>
              <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, color: '#1c1917', lineHeight: 1.3 }}>
                {row.project_number || 'ไม่ระบุโครงการ'}
              </Typography>
              {row.project_name && (
                <Typography sx={{ fontSize: '0.95rem', color: '#57534e' }} noWrap title={row.project_name}>{row.project_name}</Typography>
              )}
            </Box>
            <IconButton onClick={onClose} aria-label="ปิด"><CloseIcon /></IconButton>
          </Stack>

          <Box sx={{ mx: 3, mb: 2, display: 'flex', flexWrap: 'wrap', border: `1px solid ${BORDER}`, borderRadius: 2 }}>
            <Stat
              label="พนักงาน"
              value={`${rows.length} คน`}
              hint={[dailyCount && `รายวัน ${dailyCount}`, monthlyCount && `รายเดือน ${monthlyCount}`].filter(Boolean).join(' · ')}
            />
            <Stat label="รายได้รวม" value={fmt(total('income'))} hint="ค่าแรง + OT + เบี้ยเลี้ยง + ค่าออกสนาม + เงินได้อื่นๆ" />
            <Stat label="หักรวม" value={`−${fmt(total('deduct'))}`} color="#78716c" hint="ปกส. + ภาษี + CRL + หักอื่นๆ" />
            <Stat label="ที่ต้องจ่าย" value={fmt(row.total_payable)} color="#c2410c" />
            <Stat label="ต้นทุนโครงการ" value={fmt(row.total_spent)} hint="รายได้รวม + ปกส." />
          </Box>

          <TableContainer sx={{ px: 3, pb: 1 }}>
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'auto' }}>
              <Table sx={{ minWidth: 1180 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...HEAD_SX, py: 0.75 }} />
                    <TableCell colSpan={INCOME_COLS.length} align="center" sx={{ ...HEAD_SX, ...GROUP_EDGE, py: 0.75, color: '#15803d' }}>รายได้</TableCell>
                    <TableCell colSpan={DEDUCT_COLS.length} align="center" sx={{ ...HEAD_SX, ...GROUP_EDGE, py: 0.75, color: '#78716c' }}>หัก</TableCell>
                    <TableCell colSpan={2} align="center" sx={{ ...HEAD_SX, ...GROUP_EDGE, py: 0.75, color: '#c2410c' }}>สรุป</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ ...HEAD_SX }}>พนักงาน</TableCell>
                    {INCOME_COLS.map((c, i) => (
                      <TableCell key={c.key} align="right" sx={{ ...HEAD_SX, ...(i === 0 ? GROUP_EDGE : {}) }}>{c.label}</TableCell>
                    ))}
                    {DEDUCT_COLS.map((c, i) => (
                      <TableCell key={c.key} align="right" sx={{ ...HEAD_SX, ...(i === 0 ? GROUP_EDGE : {}) }}>{c.label}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ ...HEAD_SX, ...GROUP_EDGE }}>ที่ต้องจ่าย</TableCell>
                    <TableCell align="right" sx={{ ...HEAD_SX }}>ต้นทุน</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={INCOME_COLS.length + DEDUCT_COLS.length + 3} align="center" sx={{ ...CELL_SX, py: 5, color: '#a8a29e' }}>
                        ไม่มีข้อมูลพนักงาน
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => {
                    const pct = totalCost > 0 ? (r.cost / totalCost) * 100 : 0;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ ...CELL_SX, minWidth: 200 }}>
                          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1c1917' }}>{r.name || r.id}</Typography>
                          {r.totalDays != null ? (
                            <Typography sx={{ fontSize: '0.78rem', color: '#57534e' }}>
                              <Box component="b" sx={{ color: '#1c1917' }}>{r.projDays}/{r.totalDays}</Box>
                              {` ทำงาน ${r.projDays} วัน จาก ${r.totalDays} วัน`}
                              {r.totalDays > 0 && ` (${((r.projDays / r.totalDays) * 100).toFixed(1)}%)`}
                            </Typography>
                          ) : (
                            // งวดที่บันทึกไว้ก่อนมีข้อมูลจำนวนวัน — ใช้ข้อความเดิม
                            <Typography sx={{ fontSize: '0.78rem', color: '#a8a29e' }}>
                              {r.empType === 'FULLTIME' && 'รายเดือน · แบ่งตาม % ชั่วโมง'}
                              {r.empType && r.empType !== 'FULLTIME' && `รายวัน · ${r.days} วัน`}
                            </Typography>
                          )}
                        </TableCell>
                        {INCOME_COLS.map((c, i) => (
                          <TableCell key={c.key} align="right" sx={{ ...CELL_SX, ...NUM_SX, ...(i === 0 ? GROUP_EDGE : {}) }}>
                            <Money value={num(r[c.key])} />
                          </TableCell>
                        ))}
                        {DEDUCT_COLS.map((c, i) => (
                          <TableCell key={c.key} align="right" sx={{ ...CELL_SX, ...NUM_SX, ...(i === 0 ? GROUP_EDGE : {}) }}>
                            <Money value={num(r[c.key])} deduct color={c.key === 'crl' ? '#b91c1c' : undefined} />
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ ...CELL_SX, ...NUM_SX, ...GROUP_EDGE, fontWeight: 700 }}>
                          {r.payable == null
                            ? <Box component="span" sx={{ color: '#d6d3d1' }}>—</Box>
                            : <Box component="span" sx={{ color: r.payable >= 0 ? '#c2410c' : '#dc2626' }}>{fmt(r.payable)}</Box>}
                        </TableCell>
                        <TableCell align="right" sx={{ ...CELL_SX, ...NUM_SX }}>
                          {fmt(r.cost)}
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75} sx={{ mt: 0.5 }}>
                            <Box sx={{ width: 56, height: 4, borderRadius: 2, bgcolor: '#f5f5f4', overflow: 'hidden' }}>
                              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#f97316' }} />
                            </Box>
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: '#a8a29e', minWidth: 40, textAlign: 'right' }}>
                              {pct.toFixed(1)}%
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length > 0 && (
                    <TableRow sx={{ '& td': { bgcolor: '#fafaf9', fontWeight: 700, borderTop: `2px solid ${BORDER}`, borderBottom: 'none' } }}>
                      <TableCell sx={CELL_SX}>รวม</TableCell>
                      {INCOME_COLS.map((c, i) => (
                        <TableCell key={c.key} align="right" sx={{ ...CELL_SX, ...NUM_SX, ...(i === 0 ? GROUP_EDGE : {}) }}>{fmt(total(c.key))}</TableCell>
                      ))}
                      {DEDUCT_COLS.map((c, i) => (
                        <TableCell key={c.key} align="right" sx={{ ...CELL_SX, ...NUM_SX, color: '#78716c', ...(i === 0 ? GROUP_EDGE : {}) }}>
                          −{fmt(total(c.key))}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ ...CELL_SX, ...NUM_SX, ...GROUP_EDGE, color: '#c2410c' }}>
                        {hasPayables ? fmt(payableSum) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ ...CELL_SX, ...NUM_SX }}>{fmt(totalCost)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>

          {(!hasPayables || payableMismatch) && (
            <Box sx={{ mx: 3, mb: 2, mt: 1, px: 2, py: 1.25, borderRadius: 1.5, bgcolor: '#fff7ed', color: '#9a3412', fontSize: '0.85rem' }}>
              {!hasPayables
                ? 'งวดนี้บันทึกไว้ก่อนระบบเก็บ "ที่ต้องจ่าย" แยกรายคน จึงแสดงได้เฉพาะยอดรวมของโครงการ'
                : `ผลรวม "ที่ต้องจ่าย" รายคน (${fmt(payableSum)}) ไม่เท่ากับยอดของโครงการ (${fmt(row.total_payable)})`}
            </Box>
          )}
          <Box sx={{ height: 12 }} />
        </>
      )}
    </Dialog>
  );
}
